import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InvoiceDisplayStatusV2 } from 'anchor-scout-common/types';
import { Box, Column, DataTable, Spinner, Typography } from 'seagull';
import { Eye } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { invoiceLogicTextsPath } from '~/business-logic/invoiceLogic';
import { useInvoiceRowActions } from '~/components/InvoiceActions/useInvoiceRowActions';
import { FilterType as InvoiceFilterType, getTableFilterLabels, invoiceFilterTextsPath } from '~/components/InvoicesTable/invoicesTableFilters';
import TableFilters from '~/components/TableFilters/TableFilters';
import { isInitialFilterValue, seedNonRemovableFilterValues } from '~/components/TableFilters/TableFilters.utils';
import { useStore } from '~/contexts';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useLocaleKeys } from '~/i18n';
import { useIoContext } from '~/providers/IoContext';
import { selectors } from '~/store/state-selectors';
import styles from './ClientInvoicesListView.module.scss';
import { clientInvoicesFilters, defaultInvoicesSort, invoiceColumnTexts, invoiceColumns, invoiceHref, invoiceIntegrations, invoiceIntegrationTooltip, invoiceMainColumn, invoiceMobileFields, invoiceStatusSkins, toInvoiceRows, visibleInvoices } from './ClientInvoicesListView.table.utils';
import { distinctAgreementCount } from './ClientInvoicesListView.utils';
const clientInvoicesListViewTextsPath = 'client.detail.tabContent.issuedInvoices';
export const GetClientInvoicesListViewTID = (parentTestId)=>getTestIds(parentTestId);
export function ClientInvoicesListView(param) {
    let { invoices, isLoading, customerId, testId = '', hideManagePaymentMethods = false } = param;
    const { ClientInvoicesListViewTestIds: TID } = useMemo(()=>GetClientInvoicesListViewTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys(clientInvoicesListViewTextsPath);
    const { t: invoiceTexts } = useLocaleKeys(invoiceFilterTextsPath);
    const { t: invoiceLogicTexts } = useLocaleKeys(invoiceLogicTextsPath);
    const { t: tableRowTexts } = useLocaleKeys('invoice.tableRow');
    const navigate = useNavigate();
    const { getNow } = useIoContext();
    const rowActionsOf = useInvoiceRowActions({
        customerId
    });
    const { urlState, changeUrlState } = useStateFromUrl({
        status: {
            defaultValue: '',
            applyDefaultOnlyOnMount: true
        }
    });
    const initialStatusFromUrl = useMemo(()=>{
        const raw = urlState.status;
        if (!raw) return [];
        return raw.split(',').map((s)=>s.trim()).filter((s)=>Object.values(InvoiceDisplayStatusV2).includes(s));
    }, [
        urlState.status
    ]);
    const [search, setSearch] = useState('');
    const [selectedFilters, setSelectedFilters] = useState(()=>initialStatusFromUrl.length > 0 ? {
            [InvoiceFilterType.InvoiceStatus]: initialStatusFromUrl
        } : {});
    const showAgreementName = useMemo(()=>distinctAgreementCount(invoices) > 1, [
        invoices
    ]);
    const [sort, setSort] = useState(()=>defaultInvoicesSort(showAgreementName));
    const seedStripped = useRef(false);
    useEffect(()=>{
        if (seedStripped.current) return;
        seedStripped.current = true;
        if (urlState.status) changeUrlState({
            status: ''
        });
    }, [
        urlState.status
    ]);
    const filterLabels = useMemo(()=>getTableFilterLabels(invoiceTexts.filterLabels), []);
    const isVendor = useStore(selectors.user.isVendor);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { filterConfig, activeFilterGroups } = useMemo(()=>clientInvoicesFilters({
            invoices,
            invoiceTexts,
            isVendor,
            showQboIntegration,
            showXeroIntegration
        }), [
        invoices,
        isVendor,
        showQboIntegration,
        showXeroIntegration
    ]);
    const displayedFilters = useMemo(()=>_object_spread({}, seedNonRemovableFilterValues(activeFilterGroups, filterConfig), selectedFilters), [
        activeFilterGroups,
        filterConfig,
        selectedFilters
    ]);
    const hasActiveFilters = search !== '' || Object.entries(displayedFilters).some((param)=>{
        let [filter, value] = param;
        return !isInitialFilterValue(value, filterConfig[filter]);
    });
    const visible = useMemo(()=>visibleInvoices({
            invoices,
            search,
            filters: displayedFilters,
            now: getNow(),
            sort,
            showAgreementName
        }), [
        invoices,
        search,
        sort,
        showAgreementName,
        displayedFilters
    ]);
    const invoicesById = useMemo(()=>new Map(visible.map((invoice)=>[
                invoice.id,
                invoice
            ])), [
        visible
    ]);
    const rows = useMemo(()=>toInvoiceRows({
            invoices: visible,
            texts: invoiceLogicTexts,
            adHocAgreementName: tableRowTexts.invoiceWithoutAgreement()
        }), [
        visible
    ]);
    const statusSkins = useMemo(()=>invoiceStatusSkins({
            invoices: visible,
            texts: invoiceLogicTexts
        }), [
        visible
    ]);
    const columnTexts = useMemo(()=>invoiceColumnTexts(texts), []);
    const mainColumn = useMemo(()=>invoiceMainColumn({
            texts: columnTexts,
            integrations: (row)=>invoiceIntegrations({
                    invoice: invoicesById.get(row.id),
                    showQbo: showQboIntegration,
                    showXero: showXeroIntegration
                }),
            integrationTooltip: invoiceIntegrationTooltip
        }), [
        columnTexts,
        invoicesById,
        showQboIntegration,
        showXeroIntegration
    ]);
    const columns = useMemo(()=>invoiceColumns({
            texts: columnTexts,
            showAgreementName,
            statusSkin: (status)=>{
                var _statusSkins_get;
                return (_statusSkins_get = statusSkins.get(status)) !== null && _statusSkins_get !== void 0 ? _statusSkins_get : 'default';
            }
        }), [
        columnTexts,
        showAgreementName,
        statusSkins
    ]);
    const clearFilters = ()=>{
        setSearch('');
        setSelectedFilters({});
        if (urlState.status) changeUrlState({
            status: ''
        });
    };
    const changeFilters = (next)=>{
        setSelectedFilters(next);
        if (urlState.status) changeUrlState({
            status: ''
        });
    };
    const mobileConfig = useMemo(()=>invoiceMobileFields(showAgreementName, columnTexts), [
        showAgreementName,
        columnTexts
    ]);
    const columnMenu = useTableColumnMenu('client_invoices');
    const viewInvoice = (row)=>[
            {
                icon: Eye,
                label: tableRowTexts.viewInvoice(),
                onClick: ()=>navigate(invoiceHref(invoicesById.get(row.id))),
                bi: {
                    name: 'view_invoice'
                }
            }
        ];
    const rowMenu = (row)=>{
        const invoice = invoicesById.get(row.id);
        return invoice == null ? [] : rowActionsOf({
            invoice,
            hideManagePaymentMethods
        });
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: TID.Root(),
            children: /*#__PURE__*/ _jsx(Spinner, {
                testId: TID.Loader()
            })
        });
    }
    if (invoices.length === 0) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: TID.Root(),
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: TID.Empty(),
                skin: "standard",
                children: texts.emptyMessage()
            })
        });
    }
    return /*#__PURE__*/ _jsxs(Column, {
        testId: TID.Root(),
        gap: 16,
        className: styles.root,
        children: [
            /*#__PURE__*/ _jsx(TableFilters, {
                testIdPrefix: TID.Root(),
                filterLabels: filterLabels,
                filterConfig: filterConfig,
                selectedFilters: displayedFilters,
                setSelectedFilters: changeFilters,
                filterGroups: activeFilterGroups,
                onRefresh: ()=>undefined,
                onClear: hasActiveFilters ? clearFilters : undefined,
                search: {
                    bi: {
                        name: 'client_invoices_search'
                    },
                    value: search,
                    placeholder: texts.searchPlaceholder(),
                    testId: TID.Search(),
                    onChange: (event)=>setSearch(event.target.value),
                    onClear: ()=>setSearch('')
                }
            }),
            rows.length === 0 ? /*#__PURE__*/ _jsx(Box, {
                children: /*#__PURE__*/ _jsx(Typography, {
                    testId: TID.NoResults(),
                    skin: "secondary",
                    children: texts.noResults()
                })
            }) : /*#__PURE__*/ _jsx(DataTable, {
                allowColumnResize: true,
                allowColumnReorder: true,
                reorderBi: {
                    name: 'client_invoices_reorder_columns'
                },
                allowColumnHide: true,
                columnMenu: columnMenu,
                testId: TID.Table(),
                items: rows,
                getRowId: (row)=>row.id,
                mobile: mobileConfig,
                mainColumn: mainColumn,
                columns: columns,
                rowHref: (row)=>invoiceHref(invoicesById.get(row.id)),
                sort: _object_spread_props(_object_spread({}, sort), {
                    onChange: setSort
                }),
                rowActions: {
                    menuBi: {
                        name: 'client_invoices_actions'
                    },
                    menuSubtitle: (row)=>row.agreementName,
                    inline: viewInvoice,
                    menu: rowMenu
                }
            })
        ]
    });
}
